import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save, RefreshCw, Tags } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { getRoomTags, createRoomTag, updateRoomTag, deleteRoomTag } from '../../../lib/rooms';
import { toast } from 'sonner';

interface TagItem { id: number; name: string; is_active?: number | boolean }

const RoomTagsSettings: React.FC = () => {
  const [tags, setTags] = useState<TagItem[]>([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const t = await getRoomTags();
      setTags(t);
    } catch (e) {
      toast.error('Tags konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addTag = async () => {
    if (newName.trim() === '') return;
    try {
      const created = await createRoomTag(newName.trim());
      setTags(prev => [...prev, created]);
      setNewName('');
      toast.success('Tag hinzugefügt');
    } catch (e) {
      toast.error('Tag konnte nicht angelegt werden');
    }
  };

  const saveTagName = async (id: number, name: string) => {
    try {
      const updated = await updateRoomTag(id, { name });
      setTags(prev => prev.map(t => t.id === id ? updated : t));
      toast.success('Tag gespeichert');
    } catch (e) {
      toast.error('Tag konnte nicht gespeichert werden');
    }
  };

  const toggleActive = async (id: number, isActive: boolean) => {
    try {
      const updated = await updateRoomTag(id, { is_active: isActive });
      setTags(prev => prev.map(t => t.id === id ? updated : t));
      toast.success(isActive ? 'Tag aktiviert' : 'Tag deaktiviert');
    } catch (e) {
      toast.error('Status konnte nicht geändert werden');
    }
  };

  const removeTag = async (id: number) => {
    try {
      await deleteRoomTag(id);
      setTags(prev => prev.filter(t => t.id !== id));
      toast.success('Tag gelöscht');
    } catch (e) {
      toast.error('Tag konnte nicht gelöscht werden');
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Räume – Ausstattungstags</h1>
        <p className="text-muted-foreground">Systemweite Tags verwalten</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center"><Tags className="mr-2 h-5 w-5" />Tags</CardTitle>
          <CardDescription>Erstellen, bearbeiten und löschen Sie Ausstattungs-Tags</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input placeholder="Neuer Tagname" value={newName} onChange={e => setNewName(e.target.value)} />
            <Button onClick={addTag} disabled={loading}><Plus className="mr-2 h-4 w-4" />Hinzufügen</Button>
            <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className="mr-2 h-4 w-4" />Aktualisieren</Button>
          </div>
          <div className="divide-y rounded border">
            {tags.map(t => (
              <div key={t.id} className="flex items-center gap-2 p-2">
                <Input className="flex-1" defaultValue={t.name} onBlur={(e) => saveTagName(t.id, e.target.value)} />
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={t.is_active !== 0 && t.is_active !== false}
                    onChange={(e) => toggleActive(t.id, e.target.checked)}
                  />
                  aktiv
                </label>
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (!confirm('Tag wirklich löschen? (historische Zuweisungen bleiben sonst erhalten)')) return;
                    void removeTag(t.id);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {tags.length === 0 && <div className="p-4 text-sm text-muted-foreground">Keine Tags vorhanden</div>}
          </div>
        </CardContent>
        <CardFooter>
          <div className="text-xs text-muted-foreground">Änderungen werden sofort gespeichert.</div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default RoomTagsSettings;


